import React, { useEffect, useRef, useState } from "react";
import { useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
  type ExpressionSpecification,
  type StyleSpecification,
} from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "@maplibre/maplibre-gl-leaflet";
import L, { LatLng, type LeafletMaplibreGLOptions } from "leaflet";
import * as Sentry from "@sentry/react";
import { createRoot, type Root } from "react-dom/client";
import { LAYER_Z_INDEX } from "@/features/map/constants/layerZIndex";
import { RoadPopupContent } from "@/features/map/components/RoadPopupContent";
import {
  DEFAULT_ROAD_COLOR,
  ROAD_CLASS_COLORS,
  ROAD_LAYER_MODE_WIDTH,
  ROAD_PROPERTY_CLASS,
  ROAD_PROPERTY_WIDTH,
  ROAD_TILE_HOST,
  type RoadLayerMode,
  ROAD_WIDTH_COLORS,
} from "@/features/map/constants/roadLayer";
import "../styles/layer.css";

interface Props {
  visible: boolean;
  initialZoomLevel: number;
  mode: RoadLayerMode;
}

// 道路タイルが配信されているズーム範囲。Leaflet とのズーム差を踏まえて表示判定で使う
const MIN_ZOOM = 16;
const MAX_ZOOM = 22;

// 実際に生成・配信されている最深ズーム。z19 以上は inode 上限の都合で生成していないため、
// ソースの maxzoom はここに合わせ、これより深い表示では MapLibre が z18 を overzoom する。
// (MAX_ZOOM は表示・スタイル用のズームで、タイルの実在範囲とは別物)
const SOURCE_MAX_ZOOM = 18;

const SOURCE_NAME = "road";
const SOURCE_LAYER_NAME = "road";
const ROAD_LINE_LAYER_ID = "road_line";
// 細い線だとクリックしづらいので、見た目とは別に透明な太い線を重ねる
const ROAD_HIT_AREA_LAYER_ID = "road_hit_area";
const ROAD_ATTRIBUTION =
  '<a href="https://nlftp.mlit.go.jp/ksj/other/agreement.html">国土数値情報</a>';

const getColorMap = (mode: RoadLayerMode): Record<string, string> => {
  return mode === ROAD_LAYER_MODE_WIDTH ? ROAD_WIDTH_COLORS : ROAD_CLASS_COLORS;
};

const getPropertyName = (mode: RoadLayerMode): string => {
  return mode === ROAD_LAYER_MODE_WIDTH
    ? ROAD_PROPERTY_WIDTH
    : ROAD_PROPERTY_CLASS;
};

const createMatchExpression = (
  mode: RoadLayerMode
): ExpressionSpecification => {
  const propertyName = getPropertyName(mode);
  const colors = getColorMap(mode);
  const expression: unknown[] = ["match", ["to-string", ["get", propertyName]]];

  Object.entries(colors).forEach(([code, color]) => {
    expression.push(code, color);
  });

  expression.push(DEFAULT_ROAD_COLOR);
  return expression as ExpressionSpecification;
};

const createMaplibreStyle = (mode: RoadLayerMode): StyleSpecification => ({
  version: 8,
  name: "Road Vector Tile Layer",
  sources: {
    road: {
      type: "vector",
      tiles: [`${ROAD_TILE_HOST}/{z}/{x}/{y}.pbf`],
      minzoom: MIN_ZOOM,
      maxzoom: SOURCE_MAX_ZOOM,
      attribution: ROAD_ATTRIBUTION,
    },
  },
  layers: [
    {
      id: ROAD_LINE_LAYER_ID,
      type: "line",
      source: SOURCE_NAME,
      "source-layer": SOURCE_LAYER_NAME,
      paint: {
        "line-color": createMatchExpression(mode),
        "line-opacity": 0.9,
        "line-width": [
          "interpolate",
          ["linear"],
          ["zoom"],
          MIN_ZOOM,
          4,
          MAX_ZOOM,
          13,
        ],
      },
      minzoom: MIN_ZOOM,
      maxzoom: MAX_ZOOM,
    },
    {
      id: ROAD_HIT_AREA_LAYER_ID,
      type: "line",
      source: SOURCE_NAME,
      "source-layer": SOURCE_LAYER_NAME,
      paint: {
        "line-color": "#000000",
        "line-opacity": 0,
        "line-width": [
          "interpolate",
          ["linear"],
          ["zoom"],
          MIN_ZOOM,
          8,
          MAX_ZOOM,
          16,
        ],
      },
      minzoom: MIN_ZOOM,
      maxzoom: MAX_ZOOM,
    },
  ],
});

/*
道路データレイヤーのコンポーネント
国土数値情報のベクタータイルを MapLibre で描画し、Leaflet の地図上に重ねる
モード（道路分類種別 / 幅員区分）が切り替わるとスタイルを丸ごと差し替えるため、
レイヤー自体を作り直す作りになっている。
ROAD_TILE_HOST が空のときは呼び出し側でこのコンポーネント自体を出さない前提
*/
export const RoadLayer: React.FC<Props> = ({
  visible,
  initialZoomLevel,
  mode,
}) => {
  const map = useMap();
  const layerRef = useRef<L.MaplibreGL | null>(null);
  const popupRef = useRef<L.Popup | null>(null);
  // ポップアップ内に作った React Root。閉じるときに unmount しないと中の Reactツリーが残ってリークする
  const popupRootRef = useRef<Root | null>(null);
  const [zoomLevel, setZoomLevel] = useState<number>(initialZoomLevel);

  // 表示中のポップアップを閉じ、中身の React Root も後始末する。
  // root.unmount を render フェーズ中に呼ぶと React に警告されるので、マイクロタスクに逃がしている
  const closePopup = (): void => {
    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }
    if (popupRootRef.current) {
      const root = popupRootRef.current;
      popupRootRef.current = null;
      queueMicrotask(() => {
        root.unmount();
      });
    }
  };

  const addLayerToMap = (
    vectorTileLayer: L.MaplibreGL,
    leafletMap: L.Map
  ): void => {
    if (leafletMap.hasLayer(vectorTileLayer)) {
      return;
    }

    vectorTileLayer.addTo(leafletMap);
    /*
      addTo で非表示から表示に切り替えた直後、地図をスクロールするまでタイルが描画されない不具合がある。
      leaflet-maplibre-gl 内部の _update を直接呼んで強制的に再描画させる。
      _update は型定義に載っていない内部APIなので、必要なメソッドだけ型をつけてキャストしている。
      （LandPriceTileLayer / AreaUsePurposeTileLayer も同じ対応）
    */
    (vectorTileLayer as L.MaplibreGL & { _update: () => void })._update();

    const container = vectorTileLayer.getContainer();
    container.style.position = "absolute";
    container.style.left = "0";
    container.style.top = "0";
    container.style.zIndex = String(LAYER_Z_INDEX.ROAD);

    const maplibreMap = vectorTileLayer.getMaplibreMap();
    maplibreMap._showCollisionBoxes = false;
    maplibreMap._showTileBoundaries = false;

    // タイル配信側の障害（5xx）だけ Sentry に送る。
    // 4xx・AbortError・ネットワーク切断などのクライアント側の事象は通知対象外
    maplibreMap.on("error", (event) => {
      if (!(event.error instanceof Error)) return;
      const status = (event.error as { status?: number }).status;
      if (status === undefined || status < 500) return;

      Sentry.captureException(event.error, {
        tags: { layer: "road" },
        extra: { status },
      });
    });

    maplibreMap.on("click", ROAD_HIT_AREA_LAYER_ID, (event) => {
      // Leaflet 側のマップクリックに伝播させない（円の描画など他の挙動とぶつからないように）
      event.originalEvent.stopPropagation();

      const feature = event.features?.[0];
      if (!feature) {
        return;
      }

      const roadClassCode = String(
        feature.properties?.[ROAD_PROPERTY_CLASS] ?? ""
      );
      const roadWidthCode = String(
        feature.properties?.[ROAD_PROPERTY_WIDTH] ?? ""
      );
      const coordinates = new LatLng(event.lngLat.lat, event.lngLat.lng);

      // 連続クリック対策。前のポップアップが残っていたら閉じてから新しいのを開く
      closePopup();

      const popupHTML = document.createElement("div");
      const root = createRoot(popupHTML);
      root.render(
        <RoadPopupContent
          roadClassCode={roadClassCode}
          roadWidthCode={roadWidthCode}
        />
      );
      popupRootRef.current = root;

      popupRef.current = L.popup({ content: popupHTML, minWidth: 300 })
        .setLatLng([coordinates.lat, coordinates.lng])
        .openOn(leafletMap);
    });

    // 円の描画ツールなどでドラッグした時に MapLibre 側のパンが動いてしまうのを防ぐ
    maplibreMap.dragPan.disable();
  };

  // ズームの監視はマップが生えた時に一度だけ登録すれば済む。
  // mode 切替でレイヤーを作り直すたびにリスナを付け直したくないので、別の useEffect に分けている
  useEffect(() => {
    if (!map) return;

    const handleZoomEnd = (): void => {
      setZoomLevel(map.getZoom());
    };
    map.on("zoomend", handleZoomEnd);

    return () => {
      map.off("zoomend", handleZoomEnd);
    };
  }, [map]);

  // mode が変わるとスタイル定義そのものを差し替えたいので、レイヤーごと作り直す。
  // visible は意図的に依存から外している（トグルのたびに作り直すと無駄なのと、
  // 表示／非表示は下の useEffect で add/remove だけ切り替える方針）
  useEffect(() => {
    if (!map) return;

    const options: LeafletMaplibreGLOptions = {
      interactive: true,
      minZoom: MIN_ZOOM,
      maxZoom: MAX_ZOOM,
      // Leaflet と同調しなくなるため MapLibre 側のスクロールズームは無効化
      scrollZoom: false,
      style: createMaplibreStyle(mode),
      canvasContextAttributes: {
        // 画像エクスポート用に WebGL バッファを保持
        preserveDrawingBuffer: true,
      },
    };
    const vectorTileLayer = L.maplibreGL(options);
    layerRef.current = vectorTileLayer;

    return () => {
      closePopup();
      // この effect で生成したインスタンスを確実に消す。
      // ref 経由だと将来別の書き込み手が増えたときに取り違える恐れがあるため、
      // クロージャで掴んだ vectorTileLayer を直接除去する
      map.removeLayer(vectorTileLayer);
      if (layerRef.current === vectorTileLayer) {
        layerRef.current = null;
      }
    };
  }, [map, mode]);

  // チェックボックスの ON/OFF とズームレベルでレイヤーの表示を切り替える。
  // ベクトルタイルは Leaflet（ラスタタイル）よりもズームレベルが 1 小さいので、その分を引いて判定する
  useEffect(() => {
    if (!map || !layerRef.current) return;

    if (visible && zoomLevel - 1 >= MIN_ZOOM && zoomLevel - 1 <= MAX_ZOOM) {
      addLayerToMap(layerRef.current, map);
    } else {
      if (map.hasLayer(layerRef.current)) {
        map.removeLayer(layerRef.current);
      }
      closePopup();
    }
  }, [visible, zoomLevel, map, mode]);

  return null;
};
